import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabaseClient';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // Impersonation state
  const [originalProfile, setOriginalProfile] = useState(null); // The actual logged-in user
  const [impersonatedProfile, setImpersonatedProfile] = useState(null); // The user being impersonated
  const [activeLogId, setActiveLogId] = useState(null); // Current impersonation log ID
  const [isImpersonating, setIsImpersonating] = useState(false);

  useEffect(() => {
    // Check active sessions and sets the user
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        loadProfile(session.user.id);
      } else {
        setLoading(false);
      }
    });

    // Listen for changes on auth state (sign in, sign out, etc.)
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        loadProfile(session.user.id);
      } else {
        setProfile(null);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const loadProfile = async (userId) => {
    try {
      const { data, error } = await supabase.from('profiles').select('*').eq('id', userId).single();

      if (error) throw error;
      setProfile(data);

      // Check for active impersonation session
      if (data.role === 'super_admin') {
        await checkActiveImpersonation(data);
      }
    } catch (error) {
      console.error('Error loading profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkActiveImpersonation = async (userProfile) => {
    try {
      // Query for active impersonation sessions
      const { data: activeLog, error: logError } = await supabase
        .from('impersonation_log')
        .select('*')
        .eq('impersonator_id', userProfile.id)
        .is('ended_at', null)
        .order('started_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (logError) {
        console.error('Error checking impersonation:', logError);
        return;
      }

      if (activeLog) {
        // Check if session is stale (older than 24 hours)
        const sessionAge = Date.now() - new Date(activeLog.started_at).getTime();
        const TWENTY_FOUR_HOURS = 24 * 60 * 60 * 1000;

        if (sessionAge > TWENTY_FOUR_HOURS) {
          // Auto-close stale session
          console.log('Closing stale impersonation session');
          await supabase
            .from('impersonation_log')
            .update({ ended_at: new Date().toISOString() })
            .eq('id', activeLog.id);
          return;
        }

        // Load the target user's profile
        const { data: targetProfile, error: profileError } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', activeLog.target_user_id)
          .single();

        if (profileError) {
          console.error('Error loading impersonated profile:', profileError);
          return;
        }

        // Restore impersonation state
        setOriginalProfile(userProfile);
        setImpersonatedProfile(targetProfile);
        setActiveLogId(activeLog.id);
        setIsImpersonating(true);
      }
    } catch (error) {
      console.error('Error checking active impersonation:', error);
    }
  };

  const signIn = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { data, error };
  };

  const signUp = async (email, password, metadata = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: metadata,
      },
    });
    return { data, error };
  };

  const signOut = async () => {
    // If impersonating, end impersonation first
    if (isImpersonating) {
      await stopImpersonation();
    }

    const { error } = await supabase.auth.signOut();
    return { error };
  };

  const getDefaultDashboard = () => {
    // Use the current effective profile (impersonated or real)
    const effectiveProfile = isImpersonating ? impersonatedProfile : profile;
    if (!effectiveProfile) return 'player';

    const getDefaultForRole = (role) => {
      switch (role) {
        case 'player':
          return 'player';
        case 'parent':
          return 'parent';
        case 'coach':
          return 'coach';
        case 'admin':
        case 'super_admin':
          return 'admin';
        default:
          return 'player';
      }
    };

    return effectiveProfile.default_dashboard || getDefaultForRole(effectiveProfile.role);
  };

  const startImpersonation = async (targetUserId) => {
    try {
      // Only super_admins can impersonate
      if (profile?.role !== 'super_admin') {
        throw new Error('Only super admins can impersonate users');
      }

      // Prevent impersonating yourself
      if (targetUserId === profile.id) {
        throw new Error('Cannot impersonate yourself');
      }

      // Load target user's profile
      const { data: targetProfile, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', targetUserId)
        .single();

      if (profileError) throw profileError;

      // Optional: Prevent impersonating other super_admins
      if (targetProfile.role === 'super_admin') {
        throw new Error('Cannot impersonate other super admins');
      }

      // Create audit log entry
      const { data: logData, error: logError } = await supabase
        .from('impersonation_log')
        .insert({
          impersonator_id: profile.id,
          target_user_id: targetUserId,
        })
        .select()
        .single();

      if (logError) throw logError;

      // Set impersonation state
      setOriginalProfile(profile);
      setImpersonatedProfile(targetProfile);
      setActiveLogId(logData.id);
      setIsImpersonating(true);

      return { success: true, targetProfile };
    } catch (error) {
      console.error('Error starting impersonation:', error);
      return { success: false, error: error.message };
    }
  };

  const stopImpersonation = async () => {
    try {
      if (!isImpersonating || !activeLogId) {
        return { success: true };
      }

      // Update audit log to mark end time
      const { error: logError } = await supabase
        .from('impersonation_log')
        .update({ ended_at: new Date().toISOString() })
        .eq('id', activeLogId);

      if (logError) {
        console.error('Error updating impersonation log:', logError);
        // Continue anyway to stop impersonation
      }

      // Reset impersonation state
      setImpersonatedProfile(null);
      setActiveLogId(null);
      setIsImpersonating(false);
      // Note: originalProfile is kept in case we want to reference it

      return { success: true };
    } catch (error) {
      console.error('Error stopping impersonation:', error);
      return { success: false, error: error.message };
    }
  };

  // Get the effective profile (impersonated if active, otherwise real profile)
  const getEffectiveProfile = () => {
    return isImpersonating ? impersonatedProfile : profile;
  };

  const value = {
    user,
    profile: getEffectiveProfile(), // Returns impersonated profile if active, otherwise real profile
    realProfile: profile, // Always the actual logged-in user's profile
    loading,
    signIn,
    signUp,
    signOut,
    getDefaultDashboard,
    // Impersonation
    isImpersonating,
    originalProfile,
    impersonatedProfile,
    startImpersonation,
    stopImpersonation,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
